import { useField } from 'formik'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { Select, Form } from 'semantic-ui-react'
import { SelectItem } from '../../models/SelectIem'
import { buildSelectItem } from '../../utilities/commonFunctions'
import RefDataService from '../../services/RefDataService'
import { IIndustrySelectItems } from '../../models/industry'
import FWOLabel from '../form/FWOLabel'
import { LoaderContext } from '../context/FormLoader'

interface Props {
  industryFieldId: string
  subIndustryFieldId: string
  industryLabel: string
  subIndustryLabel: string
  industryTooltip?: string
  subIndustryTooltip?: string
  hasError?: (any) => void
  industryOptions?: SelectItem[]
  subIndustryOptions?: SelectItem[]
}

const getSubIndustries = (parentKey: string, options: SelectItem[]) => {
  return options.filter((item) => item.parentkey == parentKey)
}

const FWOIndustrySelector = (props: Props) => {
  const [industryField, industryMeta, industryHelpers] = useField(
    props.industryFieldId
  )

  const [subIndustryField, subIndustryMeta, subIndustryHelpers] = useField(
    props.subIndustryFieldId
  )

  const [industries, setIndustries] = useState<SelectItem[]>([])
  const [subIndustries, setSubIndustries] = useState<SelectItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const mountedRef = useRef(false)
  const loader = useContext(LoaderContext)

  const selectItems: IIndustrySelectItems = {
    industries: null,
    subIndustries: null,
  }

  const getRefData = () => {
    return Promise.all([
      RefDataService.getIndustries(),
      RefDataService.getSubIndustries(),
    ])
  }

  useEffect(() => {
    mountedRef.current = true
    if (
      mountedRef.current &&
      !props.industryOptions &&
      !props.subIndustryOptions
    ) {
      setIsLoading(true)
      loader.up()

      getRefData()
        .then((data) => {
          if (mountedRef.current) {
            Object.keys(selectItems).forEach((key, index) => {
              selectItems[key] = buildSelectItem(data[index].result, index != 1)
            })
            setIndustries(
              selectItems.industries === undefined ? [] : selectItems.industries
            )
            setSubIndustries(
              selectItems.subIndustries === undefined
                ? []
                : selectItems.subIndustries
            )
          }
        })
        .catch((error) => {
          props.hasError(error)
        })
        .finally(() => {
          if (mountedRef.current) {
            setIsLoading(false)
            loader.down()
          }
        })
    }

    return () => {
      mountedRef.current = false
    }
  }, [props.industryOptions, props.subIndustryOptions])

  return (
    <>
      <Form.Field
        error={
          industryMeta.touched &&
          industryField.value != null &&
          !!industryMeta.error
        }
      >
        <FWOLabel
          label={props.industryLabel}
          name={props.industryFieldId}
          tooltip={props.industryTooltip}
        />
        {industryMeta.touched &&
        industryField.value != null &&
        industryMeta.error ? (
          <span className="validation-error">{industryMeta.error}</span>
        ) : null}
        <Select
          loading={isLoading}
          clearable
          options={props.industryOptions ? props.industryOptions : industries}
          value={industryField.value || null}
          onChange={(e, d) => {
            industryHelpers.setValue(d.value)
            subIndustryHelpers.setValue('')
          }}
          onBlur={() => industryHelpers.setTouched(true)}
          placeholder="Business Industry"
          aria-label={props.industryLabel}
        />
      </Form.Field>
      <Form.Field
        error={
          subIndustryMeta.touched &&
          subIndustryField.value != null &&
          !!subIndustryMeta.error
        }
      >
        <FWOLabel
          label={props.subIndustryLabel}
          name={props.subIndustryFieldId}
          tooltip={props.subIndustryTooltip}
        />
        <Select
          loading={isLoading}
          clearable
          options={getSubIndustries(
            industryField.value,
            props.subIndustryOptions ? props.subIndustryOptions : subIndustries
          )}
          value={subIndustryField.value || null}
          onChange={(e, d) => subIndustryHelpers.setValue(d.value)}
          onBlur={() => subIndustryHelpers.setTouched(true)}
          placeholder="Sub-industry"
          aria-label={props.subIndustryLabel}
        />
        {subIndustryMeta.touched &&
        subIndustryField.value != null &&
        subIndustryMeta.error ? (
          <span className="validation-error">{subIndustryMeta.error}</span>
        ) : null}
      </Form.Field>
    </>
  )
}

export default FWOIndustrySelector
